/**
 * Editor for a Question Label row.
 *
 * Splits the raw "Q <label>: <question text>" line into a standalone Label field
 * (the variable name) and a Question text field. Both are editable: changing the
 * label renames the variable everywhere (including <<references>>), and changing
 * the wording rewrites the question line in the questionnaire. The two parts are
 * recombined into a single "Q <label>: <text>" content line for the save path.
 */
import React, { useEffect, useRef, useState } from "react"
import { Input } from "antd"
import { parseQuestionLabel, buildQuestionLabel } from "utils/labelUtils"

const { TextArea } = Input

interface QuestionLabelEditorProps {
  /** Full content line ("Q <label>: <text>") */
  value: string
  /** Called with the rebuilt content line when the label or wording changes */
  onChange: (newContent: string) => void
}

export const QuestionLabelEditor: React.FC<QuestionLabelEditorProps> = ({
  value,
  onChange,
}) => {
  const { label, text } = parseQuestionLabel(value)
  const [localLabel, setLocalLabel] = useState(label)
  const [localText, setLocalText] = useState(text)

  // Refs hold the latest values so the debounced emit always combines both parts.
  const labelRef = useRef(label)
  const textRef = useRef(text)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isTypingRef = useRef(false)

  // Sync from the prop when the change came from outside (not the user typing).
  useEffect(() => {
    if (!isTypingRef.current) {
      setLocalLabel(label)
      setLocalText(text)
      labelRef.current = label
      textRef.current = text
    }
  }, [label, text])

  const scheduleEmit = (): void => {
    isTypingRef.current = true
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      onChange(buildQuestionLabel(labelRef.current, textRef.current))
      isTypingRef.current = false
    }, 300)
  }

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setLocalLabel(e.target.value)
    labelRef.current = e.target.value
    scheduleEmit()
  }

  const handleTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    setLocalText(e.target.value)
    textRef.current = e.target.value
    scheduleEmit()
  }

  // Flush on blur so a value isn't lost if the user tabs away before the debounce.
  const handleBlur = (): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    onChange(buildQuestionLabel(labelRef.current, textRef.current))
    isTypingRef.current = false
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="flex flex-col gap-2">
      <div>
        <span className="text-xs font-semibold text-gray-500 block mb-1">
          Label (variable name)
        </span>
        <Input
          value={localLabel}
          onChange={handleLabelChange}
          onBlur={handleBlur}
          spellCheck={false}
          className="font-mono text-sm"
          placeholder="Label"
        />
      </div>
      <div>
        <span className="text-xs font-semibold text-gray-500 block mb-1">
          Question text
        </span>
        <TextArea
          value={localText}
          onChange={handleTextChange}
          onBlur={handleBlur}
          autoSize={{ minRows: 2 }}
          spellCheck={false}
          className="text-sm"
          style={{ resize: "none" }}
          placeholder="Question wording"
        />
      </div>
    </div>
  )
}
